import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Stack,
    Text,
    Heading,
    Divider,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as ChakraLink} from '@chakra-ui/react'
import {PromoCode, usePromoCode} from '@salesforce/retail-react-app/app/components/promo-code'
import ItemVariantProvider from '../../components/item-variant'
import CartItemVariantImage from '../../components/item-variant/item-image'
import CartItemVariantName from '../../components/item-variant/item-name'
import CartItemVariantAttributes from '../../components/item-variant/item-attributes'
import CartItemVariantPrice from '../../components/item-variant/item-price'
import PromoPopover from '@salesforce/retail-react-app/app/components/promo-popover'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {LeftArrow} from '../icons'
import useTaxRate from '../../hooks/use-tax-rate'
import PriceFormat from '../display-price/format-price'
import useLocaleSpecificUrl from '../../hooks/use-locale-specific-url'
import {useEstimatedDate} from '../../hooks/use-estimated-date'

const CartItems = ({basket}) => {
    const styles = useMultiStyleConfig('OrderSummary')
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id.toString()
                    result[`${key}`] = item
                    return result
                }, {})
            }
        }
    )

    return (
        <Stack {...styles.productWapper} divider={<Divider />}>
            {basket.productItems?.map((product, idx) => {
                const variant = {
                    ...product,
                    ...(products && products[product.productId]),
                    price: product.price
                }

                return (
                    <ItemVariantProvider key={product.productId} index={idx} variant={variant}>
                        <Flex {...styles.product}>
                            <CartItemVariantImage {...styles.productDetailImg} />
                            <Stack {...styles.productDetail}>
                                <CartItemVariantName />
                                <CartItemVariantAttributes includeQuantity />
                                <CartItemVariantPrice currency={basket?.currency} />
                            </Stack>
                        </Flex>
                    </ItemVariantProvider>
                )
            })}
        </Stack>
    )
}

CartItems.propTypes = {
    basket: PropTypes.object
}

const OrderSummary = ({
    basket,
    onClose,
    showPromoCodeForm = false,
    showCartItems = false,
    isEstimate = false
}) => {
    const styles = useMultiStyleConfig('OrderSummary')
    const {removePromoCode, ...promoCodeProps} = usePromoCode()
    const {data: taxRateResponse} = useTaxRate()
    const cartUrl = useLocaleSpecificUrl('cart')
    const estimatedDateData = useEstimatedDate({basket})

    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }
    const shippingItem = basket.shippingItems?.[0]
    const hasShippingPromos = shippingItem?.priceAdjustments?.length > 0
    const isExpress = estimatedDateData?.shippingMethod?.toLowerCase().includes('express')

    return (
        <Stack data-testid="sf-order-summary-test" {...styles.orderWrapper}>
            <ChakraLink href={cartUrl} {...styles.modLink}>
                <FormattedMessage
                    defaultMessage="Modify your order"
                    id="order_summary.link.edit_order"
                />
            </ChakraLink>

            <Box onClick={onClose}>
                <Heading {...styles.heading} id="order-summary-heading" onClick={onClose}>
                    <LeftArrow {...styles.headingIcon} />
                    <FormattedMessage
                        defaultMessage="Order Summary"
                        id="order_summary.heading.order_summary"
                    />
                </Heading>
            </Box>

            <Stack {...styles.pricingDetail} role="region" aria-labelledby="order-summary-heading">
                {showCartItems && <CartItems basket={basket} />}

                <Stack {...styles.price}>
                    <Flex {...styles.subTotal} aria-live="polite" aria-atomic="true">
                        <Text {...styles.textBold}>
                            <FormattedMessage
                                defaultMessage="Sub-total"
                                id="order_summary.label.subtotal"
                            />
                        </Text>
                        <Text {...styles.textBold}>
                            <PriceFormat
                                price={basket?.productSubTotal}
                                currency={basket?.currency}
                            />
                        </Text>
                    </Flex>

                    {basket.orderPriceAdjustments?.map((adjustment) => (
                        <Flex
                            {...styles.priceText}
                            key={adjustment.priceAdjustmentId}
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            <Text {...styles.priceTextText}>
                                <FormattedMessage
                                    defaultMessage="Discount on the order"
                                    id="order_summary.label.discountLabel"
                                />
                            </Text>
                            <Text {...styles.discountAmount}>
                                <PriceFormat price={adjustment.price} currency={basket?.currency} />
                            </Text>
                        </Flex>
                    ))}

                    <Flex {...styles.shipping} aria-live="polite" aria-atomic="true">
                        <Flex {...styles.shippingFlexBox}>
                            <Text {...styles.shippingFlexBoxText}>
                                <FormattedMessage
                                    defaultMessage="Delivery"
                                    id="order_summary.label.shipping"
                                />
                                <Text as="span" {...styles.estimatedDelivery}>
                                    <FormattedMessage
                                        defaultMessage="Estimated delivery date:"
                                        id="order_summary.label.deliverysummary"
                                    />
                                    {isExpress && estimatedDateData?.expressDate ? (
                                        <> {estimatedDateData.expressDate}</>
                                    ) : !isExpress &&
                                      estimatedDateData?.estimatedStartDate &&
                                      estimatedDateData?.estimatedEndDate ? (
                                        estimatedDateData.estimatedStartDate ===
                                        estimatedDateData.estimatedEndDate ? (
                                            <> {estimatedDateData.estimatedStartDate}</>
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage=" between {startDate} and {endDate}"
                                                id="order_summary.label.deliverydates"
                                                values={{
                                                    startDate: estimatedDateData.estimatedStartDate,
                                                    endDate: estimatedDateData.estimatedEndDate
                                                }}
                                            />
                                        )
                                    ) : null}
                                </Text>
                                {hasShippingPromos && (
                                    <Text as="span" {...styles.shippingFlexBoxSpan}>
                                        (
                                        <FormattedMessage
                                            defaultMessage="Promotion applied"
                                            id="order_summary.label.promo_applied"
                                        />
                                        )
                                    </Text>
                                )}
                            </Text>
                            {hasShippingPromos && (
                                <PromoPopover {...styles.shippingFlexBoxSpan}>
                                    <Stack>
                                        {shippingItem?.priceAdjustments?.map((adjustment) => (
                                            <Text key={adjustment.priceAdjustmentId} fontSize="sm">
                                                {adjustment.itemText}
                                            </Text>
                                        ))}
                                    </Stack>
                                </PromoPopover>
                            )}
                        </Flex>

                        {basket.shippingTotal == 0 ||
                        basket.shippingTotal == null ||
                        shippingItem?.priceAdjustments?.some(
                            ({appliedDiscount}) => appliedDiscount?.type === 'free'
                        ) ? (
                            <Text as="span" {...styles.taxPrice}>
                                <FormattedMessage
                                    defaultMessage="Free"
                                    id="order_summary.label.free"
                                />
                            </Text>
                        ) : (
                            <Text {...styles.taxPriceNormal}>
                                <PriceFormat
                                    price={basket.shippingTotal}
                                    currency={basket.currency}
                                />
                            </Text>
                        )}
                    </Flex>

                    <Flex {...styles.priceText} aria-live="polite" aria-atomic="true">
                        {/*TODO dynamically getting tax by region and language*/}
                        <Text {...styles.priceTextVAT}>
                            <FormattedMessage defaultMessage="VAT" id="order_summary.label.tax" /> (
                            {taxRateResponse?.value})
                        </Text>
                        <Text {...styles.darkText}>
                            <FormattedMessage
                                defaultMessage="Included"
                                id="order_summary.label.taxincluded"
                            />
                        </Text>
                    </Flex>
                </Stack>

                {showPromoCodeForm ? (
                    <Box {...styles.promoCode}>
                        <PromoCode {...promoCodeProps} />
                    </Box>
                ) : (
                    basket.couponItems?.length > 0 && <Divider />
                )}
                {basket.couponItems?.length > 0 && (
                    <Stack {...styles.promoStack}>
                        <Flex {...styles.promotion}>
                            <Text {...styles.promotionFlexBoxText}>
                                <FormattedMessage
                                    {...styles.priceTextVAT}
                                    defaultMessage="Coupon Number"
                                    id="order_summary.label.promotions_applied"
                                />
                                :
                                {basket.couponItems.map((item) => (
                                    <Flex key={item.couponItemId}>
                                        <Text {...styles.promoAmount}>{item.code}</Text>
                                    </Flex>
                                ))}
                            </Text>
                            <Text {...styles.appliedStyle}>
                                <FormattedMessage
                                    defaultMessage="APPLIED"
                                    id="order_summary.label.globalApplied"
                                />
                            </Text>
                        </Flex>
                        {basket.orderPriceAdjustments?.map((adjustment) => (
                            <Flex
                                key={adjustment.priceAdjustmentId}
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                <Text {...styles.priceTextText}>{adjustment.itemText}</Text>
                                <Text {...styles.discountAmount}>
                                    {`(`}
                                    <PriceFormat
                                        price={adjustment.price}
                                        currency={basket?.currency}
                                    />
                                    {`)`}
                                </Text>
                            </Flex>
                        ))}
                    </Stack>
                )}
                <Divider />

                <Stack {...styles.total}>
                    <Flex
                        {...styles.pricingDetailTotalFlexBox}
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        {isEstimate ? (
                            <Text {...styles.pricingDetailTotalFlexBoxText}>
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                            </Text>
                        ) : (
                            <Text {...styles.pricingDetailTotalFlexBoxText}>
                                <FormattedMessage
                                    defaultMessage="Order Total"
                                    id="order_summary.label.order_total"
                                />
                            </Text>
                        )}
                        <Text {...styles.pricingDetailTotalFlexBoxText}>
                            <PriceFormat
                                price={basket?.orderTotal || basket?.productTotal}
                                currency={basket?.currency}
                            />
                        </Text>
                    </Flex>
                </Stack>
            </Stack>
        </Stack>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    onClose: PropTypes.func
}

export default OrderSummary
